// Cloudflare Turnstile widget for React surfaces.
//
// Used by /start, the booking form, /pulse and the home page forms
// (reviews, contact, guide).
//
// The api.js script tag is loaded once from src/routes/__root.tsx so
// the global `window.turnstile` object is already present by the time
// any widget mounts.
//
// Render is explicit (`render: "explicit"`) instead of auto-discovery
// so the same React tree can mount/unmount/remount the widget without
// stale-element issues.
//
// СБОЙ НЕ ТИХИЙ (см. turnstile-failure.ts): любая ошибка виджета уходит в
// Sentry, а под виджетом появляется запасной текст с WhatsApp и почтой.
//
// ЛЕНИВЫЙ ЗАПУСК (startOn). После #346 на главной сразу при загрузке шли два
// challenge параллельно — контакты и гайд, — и токен приходил через 13–18 с.
// С startOn виджет не рендерится, пока человек не начал заполнять ЭТУ форму
// (первый focusin / pointerdown / touchstart внутри неё), и рендерится с
// appearance: "interaction-only": невидим, если проверка прошла без вопроса.
// Отправку форма не блокирует заранее, а ждёт токен через waitForToken.

import { useEffect, useImperativeHandle, useRef, useState, type Ref, type RefObject } from "react";
import {
  SUPPORT_EMAIL,
  SUPPORT_WHATSAPP_URL,
  TURNSTILE_FALLBACK,
  reportTurnstileFailure,
  turnstileLang,
} from "./turnstile-failure";

declare global {
  interface Window {
    turnstile?: {
      render: (
        target: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: (errorCode?: string) => void;
          "expired-callback"?: () => void;
          "timeout-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "flexible";
          appearance?: "always" | "execute" | "interaction-only";
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
      getResponse: (widgetId: string) => string | undefined;
    };
  }
}

// Public site key — safe to commit. Pairs with TURNSTILE_SECRET_KEY in
// the worker's CF Secrets. If we ever rotate, change both.
//
// The live key is bound to the widget's hostname list (yolutap.com and
// the workers.dev previews), so on localhost the widget answers 110200
// (invalid domain) and never issues a token — which makes the forms
// untestable end to end off production. `VITE_TURNSTILE_SITE_KEY` lets a
// local run swap in Cloudflare's published always-pass test key. It is read
// from .env.local (git-ignored) and is never set in the CF build, so
// production keeps the real key by falling through.
export const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY || "0x4AAAAAADekLcgCzQCEhCVx";

// Сколько ждём api.js, прежде чем признать, что до challenges.cloudflare.com
// не достучаться: 100 попыток по 100 мс.
const SCRIPT_WAIT_ATTEMPTS = 100;

type Props = {
  onToken: (token: string) => void;
  onError?: () => void;
  onExpired?: () => void;
  theme?: "light" | "dark" | "auto";
  /** Язык запасного текста. Не задан — берётся из <html lang>. */
  lang?: string;
  /**
   * Показывать ли запасной текст с контактами. Событие в Sentry уходит в
   * любом случае. Выключают там, где у экрана свой текст об этом сбое.
   */
  fallback?: boolean;
  /**
   * Форма, первое касание которой запускает проверку. Не задана — виджет
   * рендерится сразу, как раньше (/start, бронирование, пульс).
   */
  startOn?: RefObject<HTMLElement | null>;
  /** Ручка для формы: запустить проверку и дождаться токена при отправке. */
  handleRef?: Ref<TurnstileHandle>;
  /**
   * Один тихий повтор при ошибке виджета (24.10.2026, /tutors/join): первая
   * ошибка уходит в Sentry, но запасной текст не показывается — виджет
   * сбрасывается и пробует ещё раз. Вторая ошибка — как раньше.
   */
  retryOnce?: boolean;
};

export type TurnstileHandle = {
  /** Запустить проверку, если ещё не запущена (отправка без касания полей). */
  start: () => void;
  /**
   * Токен — готовый или следующий. null — не дождались за timeoutMs или
   * виджет упал (тогда под ним уже стоит запасной текст).
   */
  waitForToken: (timeoutMs?: number) => Promise<string | null>;
  /**
   * Свежий токен для отправки: готовый, если ему меньше maxAgeMs, иначе
   * виджет сбрасывается и выдаёт новый (токен одноразовый и живёт 300 с).
   */
  freshToken: (maxAgeMs?: number, timeoutMs?: number) => Promise<string | null>;
};

/** Токену больше этого — перед отправкой берём новый (Turnstile отдаёт 300 с, запас на сеть). */
export const TOKEN_FRESH_MS = 240_000;

export function TurnstileWidget({
  onToken,
  onError,
  onExpired,
  theme = "auto",
  lang,
  fallback = true,
  startOn,
  handleRef,
  retryOnce = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [started, setStarted] = useState(!startOn);
  const tokenRef = useRef<string | null>(null);
  const tokenAtRef = useRef(0);
  const failedRef = useRef(false);
  const retriedRef = useRef(false);
  // Кто ждёт токен: отправка формы раньше, чем проверка успела закончиться.
  const waitersRef = useRef<Array<(token: string | null) => void>>([]);
  const settle = (token: string | null) => {
    const waiters = waitersRef.current;
    waitersRef.current = [];
    for (const w of waiters) w(token);
  };

  const waitNext = (timeoutMs: number) =>
    new Promise<string | null>((resolve) => {
      const timer = setTimeout(() => {
        waitersRef.current = waitersRef.current.filter((w) => w !== done);
        resolve(null);
      }, timeoutMs);
      const done = (token: string | null) => {
        clearTimeout(timer);
        resolve(token);
      };
      waitersRef.current.push(done);
    });
  useImperativeHandle(
    handleRef,
    () => ({
      start: () => setStarted(true),
      waitForToken: (timeoutMs = 30000) => {
        setStarted(true);
        if (tokenRef.current) return Promise.resolve(tokenRef.current);
        // Виджет уже упал — ждать нечего, под ним стоит запасной текст.
        if (failedRef.current) return Promise.resolve(null);
        return waitNext(timeoutMs);
      },
      freshToken: (maxAgeMs = TOKEN_FRESH_MS, timeoutMs = 20000) => {
        setStarted(true);
        if (tokenRef.current && Date.now() - tokenAtRef.current < maxAgeMs) return Promise.resolve(tokenRef.current);
        const id = widgetIdRef.current;
        if (!id || !window.turnstile) return failedRef.current ? Promise.resolve(null) : waitNext(timeoutMs);
        // Старый или сгоревший токен: сбросить виджет и дождаться нового.
        tokenRef.current = null;
        failedRef.current = false;
        try {
          window.turnstile.reset(id);
        } catch {
          return Promise.resolve(null);
        }
        return waitNext(timeoutMs);
      },
    }),
    [],
  );

  // Первое касание формы запускает проверку. Слушаем и фокус (клавиатура,
  // автозаполнение), и нажатие (Safari не фокусирует кнопку по клику).
  useEffect(() => {
    const form = startOn?.current;
    if (!form || started) return;
    const go = () => setStarted(true);
    const events = ["focusin", "pointerdown", "touchstart"] as const;
    for (const e of events) form.addEventListener(e, go, { once: true, passive: true });
    return () => {
      for (const e of events) form.removeEventListener(e, go);
    };
  }, [startOn, started]);

  useEffect(() => {
    if (!started) return;
    let cancelled = false;
    let attempts = 0;
    // Один и тот же код повторно не шлём: виджет сам повторяет попытки, и
    // каждая повторная ошибка дала бы новое событие.
    const reported = new Set<string>();
    const fail = (code: string) => {
      if (cancelled) return;
      if (!reported.has(code)) {
        reported.add(code);
        reportTurnstileFailure(code);
      }
      // Один тихий повтор: сбросить виджет и попробовать снова, без запасного текста.
      if (retryOnce && !retriedRef.current && widgetIdRef.current && window.turnstile) {
        retriedRef.current = true;
        try {
          window.turnstile.reset(widgetIdRef.current);
          return;
        } catch {
          /* сбросить не вышло — ниже обычная ошибка */
        }
      }
      setFailed(true);
      failedRef.current = true;
      tokenRef.current = null;
      settle(null);
      onError?.();
    };
    function tryRender() {
      if (cancelled) return;
      if (!containerRef.current) return;
      if (!window.turnstile) {
        if (attempts++ < SCRIPT_WAIT_ATTEMPTS) {
          // api.js still loading — retry for ~10 s
          setTimeout(tryRender, 100);
        } else {
          // Скрипт так и не пришёл: блокировщик, фильтр сети или лежит
          // challenges.cloudflare.com. Раньше здесь был молчаливый return.
          fail("script_unavailable");
        }
        return;
      }
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            // Виджет ожил после повтора — запасной текст больше не нужен.
            setFailed(false);
            failedRef.current = false;
            tokenRef.current = token;
            tokenAtRef.current = Date.now();
            settle(token);
            onToken(token);
          },
          "error-callback": (errorCode?: string) => fail(errorCode ? String(errorCode) : "unknown"),
          "expired-callback": () => {
            tokenRef.current = null;
            onExpired?.();
          },
          theme,
          // Ленивый виджет невидим, пока Cloudflare не попросит человека
          // о действии: проверка без вопроса не двигает вёрстку формы.
          ...(startOn ? { appearance: "interaction-only" as const } : {}),
        });
      } catch (e) {
        // Most common cause: HMR-remount with the same DOM node.
        console.error("[turnstile] render failed", e);
        fail("render_failed");
      }
    }
    tryRender();

    return () => {
      cancelled = true;
      tokenRef.current = null;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // already gone — ignore
        }
        widgetIdRef.current = null;
      }
    };
    // Intentionally empty deps — widget is mounted once per component
    // instance. Token-callback closure captures the latest handler via
    // ref-pattern if it ever needs to change, but in practice callers
    // pass a stable setter. Перезапуск — только при старте ленивого виджета.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  return (
    <div>
      <div ref={containerRef} />
      {failed && fallback && <TurnstileFallback lang={lang} />}
    </div>
  );
}

/**
 * Запасной текст с контактами. Экспортирован отдельно: форма показывает его
 * и тогда, когда виджет отработал, а сервер ответил «проверить нечем»
 * (reason: "unavailable").
 */
export function TurnstileFallback({ lang }: { lang?: string }) {
  const t = TURNSTILE_FALLBACK[turnstileLang(lang)];
  return (
    <p
      role="alert"
      data-turnstile-fallback
      style={{ margin: "8px 0 0", fontSize: 14, lineHeight: 1.45, color: "#9B2C2C" }}
    >
      {t.before}
      <a
        href={SUPPORT_WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "underline" }}
      >
        WhatsApp
      </a>
      {t.between}
      <a href={`mailto:${SUPPORT_EMAIL}`} style={{ textDecoration: "underline" }}>
        {SUPPORT_EMAIL}
      </a>
      {t.after}
    </p>
  );
}
